import * as React from 'react';

import {StyleableCheckbox} from '../../../react-components/StyleableCheckbox'
import {Category, OptionValue, ReadableLabels} from '../ValueObjects'
import {Filter, OptionFilter, FieldFilter, ArchiveFilter, CategoryFilter, KeywordFilter, ArchivedStatus, FieldOptionValue, FilterSelectionState} from './ValueObjects'
import {removeNulls} from "../../../lib/utils"

interface FiltersListProps {
	runSearch: () => void
	keywordsChanged: (keywords:string) => void
	filterSelectionCleared:(filter:OptionFilter<OptionValue>) => void
	filterSelectionChanged: (filter:OptionFilter<OptionValue>, value:OptionValue, isSelected:boolean) => void
	categories: Category[]

	fieldFilters: FieldFilter[]
	archiveFilter: ArchiveFilter|null
	categoryFilter: CategoryFilter|null
	keywordFilter: KeywordFilter|null

	keywords: string

	optionFilterSelections:FilterSelectionState
	labels:ReadableLabels

}

export interface FilterPlaceholderProps extends FiltersListProps {
	expandedFilters:Filter[]
	filterExpanded?: (filter:Filter, isExpanded:boolean) => void
	filterClicked?: (filter:Filter) => void
}

export interface GroupedFilterPlaceholderState {
	expanded:boolean
}

export class GroupedFilterPlaceholder extends React.Component<FiltersListProps, GroupedFilterPlaceholderState> {

	constructor(props?:FilterPlaceholderProps, context?:any) {
		super(props, context)
		this.state = { expanded: false }
	}

	render() {
		const expandedFilters = this.state.expanded ? this.allFilters() : []
		const className = this.state.expanded ? 'expanded' : 'collapsed';
		const onFilterClicked = () => this.setState({expanded : true})

		return <div className={`grouped-filter ${className}`}>
			<FiltersList
				{...this.props}
				filterClicked={onFilterClicked}
				expandedFilters={expandedFilters}
			/>
			<a
				href="#"
				className="toggle-collapse"
				onClick={(event) => {
					event.preventDefault()
					this.setState({expanded : !this.state.expanded})
				}}>
				<span className="label">{this.state.expanded ? 'Hide filters' : 'Show filters'}</span>
			</a>
		</div>
	}

	private allFilters():Filter[] {
		const filters:(Filter|null)[] = [
			this.props.archiveFilter,
			this.props.categoryFilter,
			this.props.keywordFilter,
		]

		return removeNulls(filters.concat(this.props.fieldFilters))
	}

}

export interface SeparateFilterPlaceholderState {
	expandedFieldFilters:Map<Filter, boolean>;
}

export class SeparateFilterPlaceholder extends React.Component<FiltersListProps, SeparateFilterPlaceholderState> {

	constructor(props?:FilterPlaceholderProps, context?:any) {
		super(props, context)
		this.state = {
			expandedFieldFilters : new Map<Filter, boolean>()
		}
	}

	render() {
		const expandedFilters = Array.from(this.state.expandedFieldFilters.entries())
			.filter((entry:[Filter, boolean]) => entry[1]) // Only include expanded filters
			.map((entry:[Filter, boolean]) => entry[0]) // Map to the Filter object, rather than the Map entry

		const onExpanded = (filter:Filter, isExpanded:boolean) => this.setState(
			(prevState) => {
				prevState.expandedFieldFilters.set(filter, isExpanded);
				return prevState;
			}
		);

		const onSelected = (filter:OptionFilter<OptionValue>, value:OptionValue, isSelected:boolean) => {
			this.setState(
				(prevState) => {
					prevState.expandedFieldFilters.set(filter, false);
					return prevState;
				}
			)

			this.props.filterSelectionChanged(filter, value, isSelected);
		}

		return <FiltersList
			{...this.props}
			filterExpanded={onExpanded}
			filterClicked={(filter) => onExpanded(filter, true)}
			filterSelectionChanged={onSelected}
			expandedFilters={expandedFilters}
		/>
	}
}

class FiltersList extends React.Component<FilterPlaceholderProps, {}> {

	constructor(props:FilterPlaceholderProps) {
		super(props);
	}

	refreshSearch():void {
		this.props.runSearch()
	}

	render():JSX.Element {
		return <form className="FilterPlaceholder" id="advanced-search-filter-form" onSubmit={(event) => { event.preventDefault(); return false }}>
			{this.renderFilters()}
		</form>
	}

	private onFilterClicked(filter:Filter) {
		const {filterClicked} = this.props
		if (filterClicked != null) {
			filterClicked(filter)
		}
	}

	protected isFilterExpanded(filter:Filter):boolean {
		return this.props.expandedFilters.indexOf(filter) != -1;
	}

	renderArchiveFilter():JSX.Element|null {
		const {archiveFilter, filterExpanded} = this.props
		if (archiveFilter == null) {
			return null;
		}

		const onToggle = filterExpanded == null
			? undefined
			: (isExpanded:boolean) => filterExpanded(archiveFilter, isExpanded)

		// Note that in the future, this will likely need to be changed to support textmanager so that it isn't
		// hard coded to "Issue". This was initially called "Issue" for VCTA who the advanced search feature was
		// original built for.
		return <FilterBlock
			name="Issue"
			id="archive-filter"
			isExpanded={this.isFilterExpanded(archiveFilter)}
			onClick={() => this.onFilterClicked(archiveFilter)}
			onToggle={onToggle}>
			<ArchiveOption
				filter={archiveFilter}
				selected={this.props.optionFilterSelections.getSelected(archiveFilter)}
				values={archiveFilter.values}
				filterSelectionCleared={() => this.props.filterSelectionCleared(archiveFilter)}
				changeCallback={(value, isChecked) => {this.props.filterSelectionChanged(archiveFilter, value, isChecked) }} />
		</FilterBlock>
	}

	renderKeywordFilter():JSX.Element|null {
		const {keywordFilter} = this.props
		if (keywordFilter == null) {
			return null;
		}

		return <FilterBlock
			name="Keywords"
			id="keywords-filter"
			isExpanded={true}
			onClick={() => this.onFilterClicked(keywordFilter)}>
			<FilterKeywords
				changeCallback={(keywords) => {this.props.keywordsChanged(keywords)}}
				keywords={this.props.keywords} />
		</FilterBlock>
	}

	renderFilters() {

		const archive = this.renderArchiveFilter();
		const category = this.renderCategoryFilter();

		if (this.props.fieldFilters.length == 0 && archive == null && category == null) {
			return null;
		}

		const optionFieldFilters = this.props.fieldFilters.map((filter:FieldFilter) => {

			const {filterExpanded} = this.props
			const onToggle = filterExpanded == null
				? undefined
				: (isExpanded:boolean) => {filterExpanded(filter, isExpanded)}

			return <FilterBlock
				name={filter.name}
				id={filter.id}
				key={filter.name}
				isExpanded={this.isFilterExpanded(filter)}
				onClick={() => this.onFilterClicked(filter)}
				onToggle={onToggle}>
				<FilterFieldOption
					filter={filter}
					selected={this.props.optionFilterSelections.getSelected(filter)}
					values={filter.values}
					filterSelectionCleared={() => this.props.filterSelectionCleared(filter)}
					changeCallback={(value:FieldOptionValue, isChecked:boolean) => { this.props.filterSelectionChanged(filter, value, isChecked) }} />
			</FilterBlock>
		});

		return <div className="filter-by">
			{archive}
			{category}
			{optionFieldFilters}
			{this.renderKeywordFilter()}
		</div>
	}

	renderCategoryFilter() {
		const {categoryFilter, filterExpanded} = this.props
		if (categoryFilter == null) {
			return null;
		}

		const onToggle = filterExpanded == null
			? undefined
			: (expanded:boolean) => { filterExpanded(categoryFilter, expanded) }

		return <FilterBlock
			name={this.props.labels.category}
			id="category"
			key="category"
			isExpanded={this.isFilterExpanded(categoryFilter)}
			onClick={() => this.onFilterClicked(categoryFilter)}
			onToggle={onToggle}>
			<CategoryOption
				filter={categoryFilter}
				selected={this.props.optionFilterSelections.getSelected(categoryFilter)}
				values={this.props.categories}
				filterSelectionCleared={() => this.props.filterSelectionCleared(categoryFilter)}
				changeCallback={(value, isChecked) => { this.props.filterSelectionChanged(categoryFilter, value, isChecked) }} />
		</FilterBlock>
	}
}

class FilterBlock extends React.Component<FilterBlockProps, {}> {
	toggle() {
		const {onToggle} = this.props
		if (onToggle != null) {
			onToggle(!this.props.isExpanded, this.props.id);
		}
	}

	render():JSX.Element {

		const name      = this.props.name;
		const className = "filter-block " + ( this.props.isExpanded ? 'expanded' : 'collapsed' );
		const blockId   = "filter-block-" + this.props.id;

		return <div className={className} id={blockId} onClick={this.props.onClick}>
			<div className="filter-header">
				{this.renderToggle()}
				<div className="filter-label"  onClick={() => {this.toggle()}}>{name}</div>
			</div>
			<div className="filter-contents">
				{this.props.children}
			</div>
		</div>
	}

	private renderToggle() {
		return this.props.onToggle == null ? null : (
			<StyleableCheckbox
				defaultChecked={this.props.isExpanded}
				onChange={() => {this.toggle()}}
				className="filter-toggle" />
		)
	}
}

interface FilterBlockProps extends React.Props<FilterComponent<{}, {}>> {
	id:string
	name:string
	isExpanded:boolean
	onToggle?:(isExpanded:boolean, id:string) => void
	onClick?:() => void
}

class FilterComponent<P, S> extends React.Component<P, S>{

}

interface FilterOptionsFieldProps<T> {
	filter:OptionFilter<T>
	selected:Set<OptionValue>
	filterSelectionCleared:() => void
	changeCallback:(value:T, isChecked:boolean) => void
	values:T[]
	className?:string
}

abstract class FilterOptionsField<T extends OptionValue> extends FilterComponent<FilterOptionsFieldProps<T>, {}> {

	protected isSelected(option:OptionValue):boolean {
		return this.props.selected.has(option)
	}

	protected renderChildren(option:T):JSX.Element|null {
		return null;
	}

	protected isChildSelected(parentOption:T):boolean {
		return false;
	}

	protected includeImplicitAllOption():boolean {
		return true;
	}

	private getInputName() {
		return `filters[${this.props.filter.id}]`;
	}

	render():JSX.Element {
		return <ul className={["filter-values", this.props.className == null ? '' : this.props.className].join(" ")}>
			{this.implicitAll()}
			{this.props.values.map((value) =>
				<FilterOptionValueComponent
					isSelected={this.isSelected(value)}
					isChildSelected={this.isChildSelected(value)}
					name={this.getInputName()}
					label={value.getLabel()}
					key={`filter-value-${this.props.filter.id}-${value.getId()}`}
					value={value.getId()}
					changeCallback={(isChecked:boolean) => this.onChange(value, isChecked)}>

					{this.renderChildren(value)}

				</FilterOptionValueComponent>
			)}
		</ul>
	}

	private implicitAll():JSX.Element|null {
		if (!this.includeImplicitAllOption()) {
			return null
		}

		return <FilterOptionValueComponent
			isSelected={this.props.selected.size == 0}
			name={this.getInputName()}
			label="All"
			value={undefined}
			changeCallback={this.props.filterSelectionCleared}
		/>
	}

	/**
	 * Normally we allow all change events to occur, but some subclasses need to prevent options being changed in certain
	 * situations.
	 */
	protected shouldAllowChange(value:T, isChecked:boolean) {
		return true;
	}

	protected onChange(value:T, isChecked:boolean) {
		if (this.shouldAllowChange(value, isChecked)) {
			this.props.changeCallback(value, isChecked)
		}
	}
}

interface FilterOptionValueComponentProps extends React.Props<FilterOptionValueComponent> {
	isSelected: boolean
	isChildSelected?: boolean
	name: string
	value: string|undefined
	label: string
	changeCallback:(isChecked:boolean) => void;
}

class FilterOptionValueComponent extends React.Component<FilterOptionValueComponentProps, {}> {
	render() {
		const classNames = [
			"filter-value",
			this.props.isSelected ? "selected" : "unselected",
			this.props.isChildSelected != null && this.props.isChildSelected ? "child-selected" : ""
		]

		return <li className={classNames.join(" ")} key={`${this.props.name}-${this.props.value}`}>
			<StyleableCheckbox
				defaultChecked={this.props.isSelected}
				name={this.props.name}
				onChange={(event) => { this.props.changeCallback((event.target as HTMLInputElement).checked) }}
				value={this.props.value}
				className="filter-option"
				label={this.props.label} />
			{this.props.children}
		</li>
	}
}

class FilterFieldOption extends FilterOptionsField<FieldOptionValue> {

}

class CategoryOption extends FilterOptionsField<Category> {

	protected includeImplicitAllOption():boolean {
		return true
	}

	protected isChildSelected(parentOption:Category):boolean {
		for (var i = 0; i < parentOption.children.length; i ++) {
			var child = parentOption.children[i];
			if (this.isSelected(child) || this.isChildSelected(child)) {
				return true;
			}
		}
		return false;
	}

	protected renderChildren(option:Category):JSX.Element {
		return <CategoryChildOption
			filter={this.props.filter}
			values={option.children}
			selected={this.props.selected}
			className="children"
			filterSelectionCleared={this.props.filterSelectionCleared}
			changeCallback={this.props.changeCallback}/>
	}

}

//todo// i think it would be good to refactor these as separate
//       classes that use the other components and provides
//       the "overrides" as params rather than inheriting from them
class CategoryChildOption extends CategoryOption {
	protected includeImplicitAllOption():boolean {
		return false
	}
}

class ArchiveOption extends FilterOptionsField<ArchivedStatus> {

	protected includeImplicitAllOption():boolean {
		return false;
	}

	/**
	 * Only propagate change events for archive options if they are selecting, not deselecting.
	 * We need one of these options to always be selected to make sense.
	 */
	protected shouldAllowChange(value:ArchivedStatus, isChecked:boolean) {
		return isChecked;
	}

}

interface KeywordFilterProps {
	keywords:string
	changeCallback:(keywords:string) => void
}

class FilterKeywords extends FilterComponent<KeywordFilterProps, {}> {

	private handleChange(event:React.FormEvent<{}>) {
		const inputElement = event.target as HTMLInputElement
		this.props.changeCallback(inputElement.value)
	};

	render():JSX.Element {
		return <div className="FilterKeywords">
			<input
				type="text"
				placeholder="Type here"
				value={this.props.keywords}
				onChange={(event) => {this.handleChange(event)}}
			/>
		</div>
	}

}



// WEBPACK FOOTER //
// ./src/news2/advancedSearch/filter/FilterPlaceholder.tsx